/**
 * Which plan of record is in force (migration 919).
 *
 * A plan moves through four states: being prepared → authorized, starts later
 * → in force (exactly one per scope) → replaced. The database works the state
 * out from authorization and the plan's takes-effect date (plan_force_states);
 * this file only reads it and calls the three setters, each of which checks
 * authority itself (the date and the plan's org node: whoever may authorize the
 * plan; a node's "own plan in force": a workspace admin).
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';

export type PlanForceState = 'being_prepared' | 'starts_later' | 'in_force' | 'replaced';

export interface PlanForceRow {
  planCycleId: string;
  state: PlanForceState;
  /** The org node the plan is in force for; null = the top. */
  scopeOrgUnitId: string | null;
  takesEffectOn: string | null;
  /** When it takes (or took) effect: its date, else the day it was authorized. Null until authorized. */
  effectiveOn: string | null;
  authorizedAt: string | null;
  /** For a replaced plan: the plan now in force in its scope. */
  replacedBy: string | null;
}

export const PLAN_FORCE_STEPS: { state: PlanForceState; label: string }[] = [
  { state: 'being_prepared', label: 'Being prepared' },
  { state: 'starts_later', label: 'Authorized, starts later' },
  { state: 'in_force', label: 'In force' },
  { state: 'replaced', label: 'Replaced' },
];

interface Row {
  plan_cycle_id: string;
  state: PlanForceState;
  scope_org_unit_id: string | null;
  takes_effect_on: string | null;
  effective_on: string | null;
  authorized_at: string | null;
  replaced_by: string | null;
}

const toRow = (r: Row): PlanForceRow => ({
  planCycleId: r.plan_cycle_id,
  state: r.state,
  scopeOrgUnitId: r.scope_org_unit_id,
  takesEffectOn: r.takes_effect_on,
  effectiveOn: r.effective_on,
  authorizedAt: r.authorized_at,
  replacedBy: r.replaced_by,
});

/** Every plan's state in a workspace. Null when it could not be read. */
export async function fetchPlanForceStates(workspaceId: string): Promise<PlanForceRow[] | null> {
  const { data, error } = await getTypedSupabaseClient().rpc('plan_force_states', { p_workspace_id: workspaceId });
  if (error || !data) return null;
  return (data as Row[]).map(toRow);
}

/**
 * The state of each of these plans, read from their own workspaces. Null when
 * it could not be read — callers then keep their earlier rule rather than guess.
 */
export async function fetchPlanStatesByCycle(planCycleIds: string[]): Promise<Map<string, PlanForceRow> | null> {
  const ids = [...new Set(planCycleIds)];
  if (ids.length === 0) return new Map();
  const { data: cycles, error } = await getTypedSupabaseClient().from('plan_cycles').select('id, workspace_id').in('id', ids);
  if (error || !cycles) return null;
  const workspaces = [...new Set(cycles.map((c) => c.workspace_id).filter((w): w is string => !!w))];
  const rows = await Promise.all(workspaces.map((w) => fetchPlanForceStates(w)));
  if (rows.some((r) => r === null)) return null;
  const out = new Map<string, PlanForceRow>();
  for (const r of rows.flat()) if (r && ids.includes(r.planCycleId)) out.set(r.planCycleId, r);
  return out;
}

/** The plan this page belongs to, read in its own workspace (not whichever workspace is open). */
export async function fetchPlanForceRow(planCycleId: string): Promise<PlanForceRow | null> {
  const { data, error } = await getTypedSupabaseClient().from('plan_cycles').select('workspace_id').eq('id', planCycleId).maybeSingle();
  if (error || !data?.workspace_id) return null;
  const rows = await fetchPlanForceStates(data.workspace_id);
  return rows?.find((r) => r.planCycleId === planCycleId) ?? null;
}

async function call(run: () => PromiseLike<{ error: { message: string } | null }>): Promise<string | null> {
  const { error } = await run();
  return error ? error.message : null;
}

/** Set the date a plan takes effect, or null for "when authorized". Returns an error message, or null. */
export function setPlanTakesEffect(planCycleId: string, takesEffectOn: string | null): Promise<string | null> {
  // A null date clears it ("when authorized"); the generated type marks the argument required.
  return call(() => getTypedSupabaseClient().rpc('set_plan_takes_effect', { p_plan_cycle_id: planCycleId, p_takes_effect_on: takesEffectOn as string }));
}

/** Say which org node a plan is for (null = the top). */
export function setPlanScope(planCycleId: string, orgUnitId: string | null): Promise<string | null> {
  return call(() => getTypedSupabaseClient().rpc('set_plan_scope', { p_plan_cycle_id: planCycleId, p_org_unit_id: orgUnitId as string }));
}

/** Allow or stop an org node having its own plan in force (workspace admin). */
export function setOrgUnitOwnPlanInForce(orgUnitId: string, allowed: boolean): Promise<string | null> {
  return call(() => getTypedSupabaseClient().rpc('set_org_unit_own_plan_in_force', { p_org_unit_id: orgUnitId, p_allowed: allowed }));
}
